import { useStore } from '@tanstack/react-form';
import { Divider, Input, LearnMoreLink, Modal, RadioSet, Text } from '@ui/themed';
import { t } from 'i18next';
import { SettingToggleRow } from '../../../../components/setting-toggle-row';
import type { Setting } from '../../../../context/settings-context';
import { useSettings } from '../../../../context/settings-context';
import type { I18nPrefix } from '../../../../i18n/prefix';
import { useAppForm } from '../../../../lib/app-form';
import { PARTNER_DOCS_URLS } from '../../constants';
import { buildOAuthAuthorizeUrl } from '../../oauth';
import { getOAuthClientIds } from '../../oauth-client-ids';
import type { PartnerModalProps } from '../../types';
import { SectionTitle } from '../section-title';
import { buildOAuthRedirectUri, generateOAuthState, OAUTH, usePartnerModalSave } from './shared';

const QUICKBOOKS = 'features:partners.components.quickbooks' satisfies I18nPrefix;
const COMMON = 'features:partners.components.common' satisfies I18nPrefix;

function startOAuthFlow(settings: Setting[]) {
  localStorage.setItem(OAUTH.quickbooks.settingsKey as string, JSON.stringify(settings));
  const state = generateOAuthState();
  localStorage.setItem(OAUTH.quickbooks.stateKey, state);
  window.location.href = buildOAuthAuthorizeUrl('https://appcenter.intuit.com/connect/oauth2', {
    response_type: 'code',
    client_id: getOAuthClientIds().quickbooks,
    redirect_uri: buildOAuthRedirectUri(OAUTH.quickbooks.redirectPath),
    scope: 'com.intuit.quickbooks.accounting',
    state,
  });
}

export function QuickBooksModal({ isOpen, onClose }: PartnerModalProps) {
  const settings = useSettings();
  const { save, isSaving } = usePartnerModalSave(onClose);

  const hasOAuthCode = !!settings.integrationQuickBooksOAuthCode;

  // Settings the API has never persisted come back `undefined`, so every boolean needs a fallback
  // before it reaches a controlled input or the PATCH payload. `autoContactCreationEnabled` keeps
  // `null` deliberately — it is the "not yet chosen" state that gates the primary action.
  const form = useAppForm({
    defaultValues: {
      ignoreTransactions: settings.ignoreTransactions ?? false,
      autoContactCreationEnabled: settings.integrationQuickBooksAutoContactCreationEnabled ?? null,
      taxExemptionTaxCode: settings.integrationQuickBooksTaxExemptionTaxCode ?? '',
      shippingTaxCode: settings.integrationQuickBooksShippingTaxCode ?? '',
      salesReceiptsEnabled:
        settings.integrationQuickBooksGenerateSalesReceiptForPaymentUpfrontEnabled ?? false,
      incomeAccountName: settings.integrationQuickBooksIncomeAccountName ?? '',
      paymentOnAccountInvoiceEvent: (settings.integrationQuickBooksPaymentOnAccountInvoiceEvent ??
        'processing') as 'processing' | 'shipped',
    },
  });

  const values = useStore(form.store, (state) => state.values);

  const isValidConfiguration = values.autoContactCreationEnabled != null;

  function getSelectedSettings(): Setting[] {
    return [
      { key: 'integrationQuickBooks', value: true },
      {
        key: 'integrationQuickBooksAutoContactCreationEnabled',
        value: values.autoContactCreationEnabled,
      },
      { key: 'integrationQuickBooksTaxExemptionTaxCode', value: values.taxExemptionTaxCode },
      { key: 'integrationQuickBooksShippingTaxCode', value: values.shippingTaxCode },
      {
        key: 'integrationQuickBooksGenerateSalesReceiptForPaymentUpfrontEnabled',
        value: values.salesReceiptsEnabled,
      },
      { key: 'integrationQuickBooksIncomeAccountName', value: values.incomeAccountName },
      {
        key: 'integrationQuickBooksPaymentOnAccountInvoiceEvent',
        value: values.paymentOnAccountInvoiceEvent,
      },
      { key: 'ignoreTransactions', value: values.ignoreTransactions },
    ];
  }

  function handleSave() {
    void save(getSelectedSettings());
  }

  return (
    <Modal
      size="large"
      isOpen={isOpen}
      onClose={onClose}
      secondaryAction={{ text: t(`${COMMON}.close`), onClick: onClose }}
      primaryAction={{
        text: hasOAuthCode ? t(`${COMMON}.save`) : t(`${QUICKBOOKS}.connect`),
        disabled: !isValidConfiguration,
        onClick: () => (hasOAuthCode ? handleSave() : startOAuthFlow(getSelectedSettings())),
        loading: isSaving,
      }}
    >
      <Modal.Header>QuickBooks</Modal.Header>
      <Modal.Body>
        <div className="flex flex-col gap-4">
          <Text variant="secondary">
            {t(`${QUICKBOOKS}.description`)} <LearnMoreLink link={PARTNER_DOCS_URLS.quickbooks} />
          </Text>

          <SettingToggleRow
            label={t(`${QUICKBOOKS}.take_payment.label`)}
            description={t(`${QUICKBOOKS}.take_payment.description`)}
            checked={values.ignoreTransactions}
            onChange={(checked) => form.setFieldValue('ignoreTransactions', checked)}
            learnMoreUrl={PARTNER_DOCS_URLS.quickbooks}
          />

          <div className="my-1">
            <Divider />
          </div>

          <div className="flex flex-col gap-3">
            <SectionTitle href={PARTNER_DOCS_URLS.quickbooks}>
              {t(`${QUICKBOOKS}.customers.title`)}
            </SectionTitle>
            <Text variant="primary">
              {t(`${QUICKBOOKS}.customers.auto_contact_creation.label`)}
            </Text>
            <Text variant="secondary">
              {t(`${QUICKBOOKS}.customers.auto_contact_creation.description`)}{' '}
              <LearnMoreLink link="https://docs.sparklayer.io/account-and-addresses#advanced-account-settings" />
            </Text>
            <RadioSet<'enabled' | 'disabled' | ''>
              name="integrationQuickBooksAutomaticContactCreation"
              value={
                values.autoContactCreationEnabled === null
                  ? ''
                  : values.autoContactCreationEnabled
                    ? 'enabled'
                    : 'disabled'
              }
              onChange={(selected) =>
                form.setFieldValue('autoContactCreationEnabled', selected === 'enabled')
              }
              options={[
                {
                  label: t(`${QUICKBOOKS}.customers.enabled.label`),
                  description: t(`${QUICKBOOKS}.customers.enabled.description`),
                  value: 'enabled',
                },
                {
                  label: t(`${QUICKBOOKS}.customers.disabled.label`),
                  description: t(`${QUICKBOOKS}.customers.disabled.description`),
                  value: 'disabled',
                },
              ]}
            />
          </div>

          <div className="my-1">
            <Divider />
          </div>

          <div className="flex flex-col gap-3">
            <SectionTitle href={PARTNER_DOCS_URLS.quickbooks}>
              {t(`${QUICKBOOKS}.invoices.title`)}
            </SectionTitle>
            <RadioSet<'processing' | 'shipped'>
              label={t(`${QUICKBOOKS}.invoices.payment_on_account.label`)}
              name="integrationQuickBooksPaymentOnAccountInvoiceEvent"
              value={values.paymentOnAccountInvoiceEvent}
              onChange={(selected) => form.setFieldValue('paymentOnAccountInvoiceEvent', selected)}
              options={[
                {
                  label: t(`${QUICKBOOKS}.invoices.payment_on_account.on_order.label`),
                  description: t(`${QUICKBOOKS}.invoices.payment_on_account.on_order.description`),
                  value: 'processing',
                },
                {
                  label: t(`${QUICKBOOKS}.invoices.payment_on_account.on_shipment.label`),
                  description: t(
                    `${QUICKBOOKS}.invoices.payment_on_account.on_shipment.description`,
                  ),
                  value: 'shipped',
                },
              ]}
            />
            <SettingToggleRow
              label={t(`${QUICKBOOKS}.invoices.sales_receipts.label`)}
              description={t(`${QUICKBOOKS}.invoices.sales_receipts.description`)}
              checked={values.salesReceiptsEnabled}
              onChange={(checked) => form.setFieldValue('salesReceiptsEnabled', checked)}
              learnMoreUrl={PARTNER_DOCS_URLS.quickbooks}
            />
          </div>

          <div className="my-1">
            <Divider />
          </div>

          <div className="flex flex-col gap-3">
            <SectionTitle href={PARTNER_DOCS_URLS.quickbooks}>
              {t(`${QUICKBOOKS}.accounts.title`)}
            </SectionTitle>
            <form.Field name="incomeAccountName">
              {(field) => (
                <Input
                  label={t(`${QUICKBOOKS}.accounts.income_account.label`)}
                  description={t(`${QUICKBOOKS}.accounts.income_account.description`)}
                  value={field.state.value}
                  onChange={field.handleChange}
                />
              )}
            </form.Field>
          </div>

          <div className="my-1">
            <Divider />
          </div>

          <div className="flex flex-col gap-3">
            <SectionTitle href={PARTNER_DOCS_URLS.quickbooks}>
              {t(`${QUICKBOOKS}.tax.title`)}
            </SectionTitle>
            <form.Field name="shippingTaxCode">
              {(field) => (
                <Input
                  label={t(`${QUICKBOOKS}.tax.shipping_tax.label`)}
                  description={t(`${QUICKBOOKS}.tax.shipping_tax.description`)}
                  value={field.state.value}
                  onChange={field.handleChange}
                />
              )}
            </form.Field>
            <form.Field name="taxExemptionTaxCode">
              {(field) => (
                <Input
                  label={t(`${QUICKBOOKS}.tax.exemption_tax.label`)}
                  description={t(`${QUICKBOOKS}.tax.exemption_tax.description`)}
                  value={field.state.value}
                  onChange={field.handleChange}
                />
              )}
            </form.Field>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}
